/**
 * Body and supporting text primitives.
 *
 * - `Pretitle` — small uppercase mono (or sans) label that sits ABOVE a
 *   heading. `variant` controls shape; `font` overrides family independently
 *   (default = whatever the variant says: mono → mono, sans → sans).
 *   Was previously named `Eyebrow`.
 * - `Lead` — large supportive paragraph under a heading.
 *   `size="md"` (default) for under-section leads; `size="lg"` for under-hero.
 * - `Body` — default running text. Sizes `xs | sm | md | lg | xl`.
 *   `md` is the page default. `xs` covers data-row / pill / micro-label
 *   density (e.g. perf side panels). The `lg` and `xl` step up only at the
 *   `lg:` breakpoint to avoid jarring bumps on tablet-class screens.
 * - `Caption` — small print, image captions, fine details.
 *   `size="sm"` (default), `xs` (≈ 11px), `micro` (≈ 10px uppercase).
 * - `Code` — inline monospace with a subtle surface tint.
 *
 * **Font defaults.** Body / Lead / Caption / H1–H4 default to `font="display"`
 * (Space Grotesk). Pass `font="sans"` to flip a region to Inter (e.g. dense
 * data tables, pricing card grids), or wrap the region in a `font-sans`
 * div and pass `font="inherit"` to skip the per-primitive font class.
 *
 * Server-component safe.
 */

import React from 'react'
import { Join } from '../../lib/utils'
import {
  fontClass,
  resolveFontClass,
  toneClass,
  type FontProp,
  type Tone,
} from './tokens'

interface BaseTextProps {
  children: React.ReactNode
  tone?: Tone
  className?: string
  id?: string
  as?: keyof React.JSX.IntrinsicElements
  /** Font family override. Most primitives default to `'display'`
   *  (Space Grotesk). Use `'inherit'` inside a `font-*` parent to skip
   *  emitting any font class so the parent's font wins. */
  font?: FontProp
}

export interface BodyProps extends BaseTextProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

// Body sizes.
//
// Notes:
// - `xs` for dense data rows / pills / micro-meta. Use `Caption` instead for
//   true captions; this is here so paragraphs can shrink without giving up the
//   `Body` semantics.
// - `md` (default) drops the redundant `sm:text-base` rule that was previously
//   in place — it was a no-op.
// - `lg` and `xl` deliberately bump at `lg:` (1024 px), not `sm:` (640 px).
//   Real consumers (codelogician.dev, imandrax.dev) only step body type up
//   once the column is genuinely wide enough.
const bodySize = {
  xs: 'text-xs leading-snug',
  sm: 'text-sm leading-relaxed',
  md: 'text-base leading-relaxed',
  /** `lg` and `xl` intentionally drop `leading-relaxed` — they're the sizes
   *  used for SectionHeader subtitles / descriptions, where the larger type
   *  already gives plenty of vertical air. Forcing 1.625 makes the block
   *  read overspaced; let the default `leading-normal` (~1.5) apply. */
  lg: 'text-base lg:text-lg',
  xl: 'text-lg lg:text-xl',
} as const

export function Body({
  children,
  tone = 'default',
  size = 'md',
  font = 'display',
  className,
  id,
  as = 'p',
}: BodyProps) {
  return React.createElement(
    as,
    {
      id,
      className: Join(
        resolveFontClass(font),
        'font-light',
        bodySize[size],
        toneClass[tone],
        className,
      ),
    },
    children,
  )
}

export interface LeadProps extends BaseTextProps {
  /** `md` (default) — section subhead (`text-base lg:text-lg`). `lg` — hero
   *  subhead (`text-xl sm:text-2xl leading-snug`). */
  size?: 'md' | 'lg'
}

const leadSize = {
  md: 'text-base leading-relaxed font-light text-pretty lg:text-lg',
  lg: 'text-xl leading-snug font-light text-pretty sm:text-2xl',
} as const

export function Lead({
  children,
  tone = 'muted',
  size = 'md',
  font = 'display',
  className,
  id,
  as = 'p',
}: LeadProps) {
  return React.createElement(
    as,
    {
      id,
      className: Join(
        resolveFontClass(font),
        leadSize[size],
        toneClass[tone],
        className,
      ),
    },
    children,
  )
}

export interface PretitleProps extends BaseTextProps {
  /** Shape (size / weight / tracking) of the pretitle. `mono` (default) is
   *  the canonical SectionHeader pretitle; `sans` is the looser pricing-style
   *  break. Note: `variant` no longer locks the font — pass `font` to flip
   *  the family independently (e.g. `variant="sans" font="display"`). */
  variant?: 'mono' | 'sans'
}

const pretitleShape = {
  mono: 'text-xs font-semibold tracking-widest uppercase',
  sans: 'text-sm font-medium tracking-wide uppercase',
} as const

/** Each variant's *default* font family. Overridden when the call site
 *  passes an explicit `font` prop. */
const pretitleVariantFont: Record<'mono' | 'sans', FontProp> = {
  mono: 'mono',
  sans: 'sans',
}

/** Small label that sits ABOVE a heading. Replaces the previously-named
 *  `Eyebrow`. */
export function Pretitle({
  children,
  tone = 'accent',
  variant = 'mono',
  font,
  className,
  id,
  as = 'p',
}: PretitleProps) {
  const resolvedFont = font ?? pretitleVariantFont[variant]
  return React.createElement(
    as,
    {
      id,
      className: Join(
        resolveFontClass(resolvedFont),
        pretitleShape[variant],
        toneClass[tone],
        className,
      ),
    },
    children,
  )
}

export interface CaptionProps extends BaseTextProps {
  /** `sm` (default) `text-xs`. `xs` ≈ 11 px. `micro` ≈ 10 px uppercase
   *  tracked — for chart legends / data side panels. */
  size?: 'sm' | 'xs' | 'micro'
}

const captionSize = {
  sm: 'text-xs leading-snug',
  xs: 'text-[11px] leading-snug tracking-wide',
  micro: 'text-[10px] leading-snug tracking-widest uppercase',
} as const

export function Caption({
  children,
  tone = 'subtle',
  size = 'sm',
  font = 'display',
  className,
  id,
  as = 'p',
}: CaptionProps) {
  return React.createElement(
    as,
    {
      id,
      className: Join(
        resolveFontClass(font),
        captionSize[size],
        toneClass[tone],
        className,
      ),
    },
    children,
  )
}

export function Code({
  children,
  tone = 'default',
  className,
  id,
}: BaseTextProps) {
  return (
    <code
      id={id}
      className={Join(
        fontClass.mono,
        'bg-surface-sunken rounded px-1.5 py-0.5 text-[0.9em]',
        toneClass[tone],
        className,
      )}
    >
      {children}
    </code>
  )
}
